// Vehicle.tsx
import React, { useMemo, useState, useEffect } from "react";
import { Form, Button, Card, Col, Row, ProgressBar, Badge, InputGroup } from "react-bootstrap";
import { useTranslate } from "../hooks/LanguageProvider";
import AdvancedSelectGroup from "../components/Vehicle/SelectGroupVehicle";
import SimpleSelectGroup from "../components/Vehicle/SelectGroup";
import Select, { SingleValue } from "react-select";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { useNavigate, useParams } from "react-router-dom";
import { Bounce, toast } from "react-toastify";
import { generateExcelFile } from "../utilities/functions";

const backendUrl = process.env.REACT_APP_BACKEND_URL;

interface VehicleInterface {
  id_vehicule: number;
  id_conducteur: number | null;
  category_vehicule: string;
  couleur_vehicule: string;
  id_groupe: number | null;
  id_user: number | null;

  immatriculation_vehicule: string;
  num_porte_vehicule: string;

  // Classification (intégration demandée)
  num_chassis_vehicule: string;      // VIN
  modele_vehicule: string;           // Marque (mapping temporaire)
  propriete_vehicule?: string;       // Référence (colonne existante dans la table)

  vehicule_type: string;

  // existants
  GPSDIST: number;
  PSN: string;

  // date (si backend accepte)
  date_aquisition_vehicule?: string | null;

  annee_vehicule?: string;
  date_circulation_vehicule?: string;
  nbre_place_vehicule?: string | number;
  nbre_porte_vehicule?: string | number;
  puissance_vehicule?: string;
  type_carburant_vehicule?: string;
  fuel_type?: string;
  etat_vehicule?: string;
  commentaire_vehicule?: string;
  longueur_vehicule?: string;
  largeur_vehicule?: string;
  hauteur_vehicule?: string;
  poid_vehicule?: string;
  ptac_vehicule?: string;
  maximum_allowed_total?: string;
  famille_vehicule?: string;
  gamme_vehicule?: string;
  consommation_moyenne_vehicule?: string;
  fuel_level_vehicule?: string;
  co2_vehicule?: string;
  capacite_res_vehicule?: string;

  companie_assurance_vehicule?: string;
  type_assurance_vehicule?: string;
  date_debut_assurance_vehicule?: string;
  date_expir_assurance_vehicule?: string;
  cout_assurance_vehicule?: string;
  delai_assurance_vehicule?: string;
  reference_assurance_vehicule?: string;
  note_assurance_vehicule?: string;
  etat_ctr_tech_vehicule?: string;
  date_debut_ctr_tech_vehicule?: string;
  date_fin_ctr_tech_vehicule?: string;
  station_ctr_vehicule?: string;
  cout_ctr_tech_vehicule?: string;
  reference_ctr_tech_vehicule?: string;
  note_ctr_tech_vehicule?: string;
  date_vignette_vehicule?: string;
  cout_vignette_vehicule?: string;
  num_vignette_vehicule?: string;

  prochain_vidange_vehicule?: string | number;
  date_dernier_vidange?: string;
  dernier_vidange_vehicule?: string | number;
  kilometrage_prochain_entretien?: string | number;
  kilometrage_reel_vehicule?: string;
  inService_vehicule?: string;
  detail_vehicule?: string;
  info_vehicule?: string | number;

  id_gps?: string | number;
  LAST_IB_CODE?: string;
  latitude_vehicule?: string;
  longitude_vehicule?: string;
  date_heure_position_vehicule?: string | null;
}

interface UserInterface {
  id_user: number;
  nom_user: string;
  prenom_user: string;
}
interface DriverInterface {
  id_conducteur: number;
  nom_conducteur: string;
  prenom_conducteur: string;
}
interface GroupInterface {
  id_groupe: number;
  nom_groupe: string;
}

type OptionType = { value: any; label: string };
type PageMode = "simple" | "advanced";
type AdvancedVehicleTab =
  | "identity"
  | "classification"
  | "assignment"
  | "technical"
  | "documents"
  | "maintenance"
  | "appearance";
type VehicleUserAffectation = {
  localId: string;
  id_user: number | null;
};

type VehicleExtraField = {
  name: keyof VehicleInterface;
  label: string;
  placeholder?: string;
  type?: string;
  unit?: string;
  step?: string;
  options?: OptionType[];
};

const Vehicle: React.FC = () => {
  const { id_vehicule } = useParams<{ id_vehicule?: string }>();
  const { translate } = useTranslate();
  const [acquisitionDateTime, setAcquisitionDateTime] = useState(new Date());
  const user_id = localStorage.getItem("userid");

  const [Vehicle, setVehicle] = useState<VehicleInterface>({
    id_vehicule: 0,
    id_conducteur: null,
    category_vehicule: "PL",
    couleur_vehicule: "#000000",
    id_groupe: null,
    id_user: null,

    immatriculation_vehicule: "",
    num_porte_vehicule: "",

    num_chassis_vehicule: "",
    modele_vehicule: "",
    propriete_vehicule: "",

    vehicule_type: "Tracteur",

    GPSDIST: 0,
    PSN: "",
    date_aquisition_vehicule: null,
  });

  const [Users, setUsers] = useState<UserInterface[]>([]);
  const [Drivers, setDrivers] = useState<DriverInterface[]>([]);
  const [groups, setGroups] = useState<GroupInterface[]>([]);

  const [typesOptions, setTypesOptions] = useState<any[]>([{ value: "Aucun", label: "Aucun" }]);
  const [groupsOptions, setGroupsOptions] = useState<any[]>([{ value: "Aucun", label: "Aucun" }]);
  const [usersOptions, setUsersOptions] = useState<any[]>([{ value: "Aucun", label: "Aucun" }]);
  const [driversOptions, setDriversOptions] = useState<any[]>([{ value: "Aucun", label: "Aucun" }]);
  const [pageMode, setPageMode] = useState<PageMode>("simple");
  const [activeAdvancedTab, setActiveAdvancedTab] = useState<AdvancedVehicleTab>("identity");
  const [affectations, setAffectations] = useState<VehicleUserAffectation[]>([]);

  const [buttonClicked, setButtonClicked] = useState(false);
  const [updateMatriculation, setUpdateMatriculation] = useState("");

  const navigate = useNavigate();
  const creatorUserId = user_id ? Number(user_id) : null;
  const hasCreatorUser = creatorUserId != null && !Number.isNaN(creatorUserId);
  const advancedVehicleFieldNames: (keyof VehicleInterface)[] = [
    "annee_vehicule",
    "date_circulation_vehicule",
    "nbre_place_vehicule",
    "nbre_porte_vehicule",
    "puissance_vehicule",
    "type_carburant_vehicule",
    "fuel_type",
    "etat_vehicule",
    "commentaire_vehicule",
    "longueur_vehicule",
    "largeur_vehicule",
    "hauteur_vehicule",
    "poid_vehicule",
    "ptac_vehicule",
    "maximum_allowed_total",
    "famille_vehicule",
    "gamme_vehicule",
    "consommation_moyenne_vehicule",
    "fuel_level_vehicule",
    "co2_vehicule",
    "capacite_res_vehicule",
    "companie_assurance_vehicule",
    "type_assurance_vehicule",
    "date_debut_assurance_vehicule",
    "date_expir_assurance_vehicule",
    "cout_assurance_vehicule",
    "delai_assurance_vehicule",
    "reference_assurance_vehicule",
    "note_assurance_vehicule",
    "etat_ctr_tech_vehicule",
    "date_debut_ctr_tech_vehicule",
    "date_fin_ctr_tech_vehicule",
    "station_ctr_vehicule",
    "cout_ctr_tech_vehicule",
    "reference_ctr_tech_vehicule",
    "note_ctr_tech_vehicule",
    "date_vignette_vehicule",
    "cout_vignette_vehicule",
    "num_vignette_vehicule",
    "prochain_vidange_vehicule",
    "date_dernier_vidange",
    "dernier_vidange_vehicule",
    "kilometrage_prochain_entretien",
    "kilometrage_reel_vehicule",
    "inService_vehicule",
    "detail_vehicule",
    "info_vehicule",
    "id_gps",
    "LAST_IB_CODE",
    "latitude_vehicule",
    "longitude_vehicule",
    "date_heure_position_vehicule",
  ];

  const getAdvancedVehicleData = (vehicle: VehicleInterface) =>
    advancedVehicleFieldNames.reduce((data, fieldName) => {
      (data as any)[fieldName] = vehicle[fieldName] as any;
      return data;
    }, {} as Partial<VehicleInterface>);

  // ===============================
  // Data loaders (inchangés)
  // ===============================
  useEffect(() => {
    if (user_id) {
      getUser(user_id);
      getGroup(user_id);
      getDriver(user_id);
    }
  }, []);

  useEffect(() => {
    if (id_vehicule) getVehicle();
  }, []);

  useEffect(() => {
    if (id_vehicule || !hasCreatorUser) return;

    setVehicle((prev) => {
      if (prev.id_user) return prev;
      getGroup(creatorUserId);
      getDriver(creatorUserId);
      return { ...prev, id_user: creatorUserId };
    });
  }, [id_vehicule, hasCreatorUser, creatorUserId]);

  useEffect(() => {
    if (id_vehicule) return;

    setAffectations((prev) => {
      if (prev.length > 0) return prev;
      return [
        {
          localId: "assignment-creator",
          id_user: creatorUserId ?? null,
        },
      ];
    });
  }, [id_vehicule, creatorUserId]);

  const getGroup = async (user_id: any) => {
    try {
      const res = await fetch(`${backendUrl}/api/groups-vehicule/groups/${user_id}`, { mode: "cors" });
      if (!res.ok) return console.error("Erreur lors de la récupération des groupes");

      const groupsData = await res.json();
      setGroups(groupsData);

      setGroupsOptions(
        groupsData.map((group: any) => ({
          value: group.id_groupe,
          label: `${group.nom_groupe || ""}`,
        })),
      );
    } catch (error) {
      console.error("Erreur lors de la récupération des groupes", error);
    }
  };

  const getUser = async (user_id: any) => {
    try {
      const res = await fetch(`${backendUrl}/api/users/find/${user_id}`, { mode: "cors" });
      if (!res.ok) return console.error("Erreur lors de la récupération des utilisateurs");

      const usersData = await res.json();
      setUsers(usersData);

      setUsersOptions(
        usersData.map((user: any) => ({
          value: user.id_user,
          label: `${user.nom_user} ${user.prenom_user || ""}`,
        })),
      );
    } catch (error) {
      console.error("Erreur lors de la récupération des utilisateurs", error);
    }
  };

  const getDriver = async (user_id: any) => {
    try {
      const res = await fetch(`${backendUrl}/api/drivers/find/${user_id}`, { mode: "cors" });
      if (!res.ok) return console.error("Erreur lors de la récupération des conducteurs");

      const driverData = await res.json();
      setDrivers(driverData);

      setDriversOptions(
        driverData.map((driver: any) => ({
          value: driver.id_conducteur,
          label: `${driver.nom_conducteur} ${driver.prenom_conducteur || ""}`,
        })),
      );
    } catch (error) {
      console.error("Erreur lors de la récupération des conducteurs", error);
    }
  };

  const getVehicle = async () => {
    try {
      const res = await fetch(`${backendUrl}/api/vehicle/find/${id_vehicule}`, { mode: "cors" });
      if (!res.ok) return console.error("Error while retrieving the vehicle");

      const data = await res.json();

      setVehicle((prev) => ({
        ...prev,
        id_vehicule: data.id_vehicule,
        id_conducteur: data.id_conducteur ?? data.id_conducteur_vehicule ?? null,
        category_vehicule: data.category_vehicule,
        couleur_vehicule: data.couleur_vehicule,
        id_groupe: data.id_groupe,
        id_user: data.id_user,
        immatriculation_vehicule: data.immatriculation_vehicule,
        num_porte_vehicule: data.num_porte_vehicule,
        vehicule_type: data.vehicule_type,
        GPSDIST: data.GPSDIST ?? 0,
        PSN: data.PSN ?? "",

        // ✅ si l’API renvoie un jour ces champs, on les prend directement
        num_chassis_vehicule: data.num_chassis_vehicule ?? prev.num_chassis_vehicule,
        modele_vehicule: data.modele_vehicule ?? prev.modele_vehicule,
        propriete_vehicule: data.propriete_vehicule ?? prev.propriete_vehicule,
        date_aquisition_vehicule: data.date_aquisition_vehicule ?? prev.date_aquisition_vehicule,
        ...advancedVehicleFieldNames.reduce((advancedData, fieldName) => {
          if (data[fieldName] !== undefined) {
            (advancedData as any)[fieldName] = data[fieldName];
          }
          return advancedData;
        }, {} as Partial<VehicleInterface>),
      }));

      setUpdateMatriculation(data.immatriculation_vehicule);

      await loadVehicleAffectations(data);

      try {
        const res = await fetch(`${backendUrl}/api/vehicle/type-options/${data.category_vehicule}`, { mode: "cors" });
        if (!res.ok) return console.error("Erreur lors de la récupération des types");

        const typesData = await res.json();
        const typesOptionsData = typesData.model.split(",").map((type: any) => ({
          value: type,
          label: `${type || ""}`,
        }));
        setTypesOptions(typesOptionsData);
      } catch (error) {
        console.error("Erreur lors de la récupération des types", error);
      }
    } catch (error) {
      console.error("Erreur lors de la récupération du véhicule", error);
    }
  };

  // ===============================
  // Handlers
  // ===============================
  const handleVehicleChange = (updatedValues: Partial<VehicleInterface>) => {
    setVehicle((prev) => ({ ...prev, ...updatedValues }));
  };

  const handleChange = (e: any) => {
    const { name, value } = e.target;
    setVehicle((prevData) => ({ ...prevData, [name]: value }));
  };

  const handleDriverChange = (selectedOption: OptionType | null) => {
    setVehicle((prev) => ({ ...prev, id_conducteur: selectedOption ? selectedOption.value : null }));
  };

  const handleGroupChange = (selectedOption: OptionType | null) => {
    setVehicle((prev) => ({ ...prev, id_groupe: selectedOption ? selectedOption.value : null }));
  };

  const handleUserChange = (selectedOption: OptionType | null) => {
    setVehicle((prev) => ({
      ...prev,
      id_user: selectedOption ? selectedOption.value : null,
    }));

    if (selectedOption) {
      getGroup(selectedOption.value);
      getDriver(selectedOption.value);
    }
  };

  const getOptionByValue = (options: OptionType[], value: any) => {
    return options.find((option) => String(option.value) === String(value));
  };

  const selectedAffectationUsers = affectations
    .map((affectation) => getOptionByValue(usersOptions, affectation.id_user))
    .filter((option): option is OptionType => Boolean(option));

  const availableAffectationUsers = usersOptions.filter(
    (option) => !affectations.some((affectation) => String(affectation.id_user) === String(option.value)),
  );

  const loadVehicleAffectations = async (vehicleData: Partial<VehicleInterface>) => {
    if (!id_vehicule) return;

    try {
      const res = await fetch(`${backendUrl}/api/vehicle-affectations/${id_vehicule}`, { mode: "cors" });
      if (!res.ok) return;

      const data = await res.json();
      if (!Array.isArray(data) || data.length === 0) {
        setAffectations([]);
        return;
      }

      const nextAffectations = data.map((affectation: any) => ({
        localId: String(affectation.id_affectation),
        id_user: affectation.id_user ?? null,
      }));

      setAffectations(nextAffectations);

    } catch (error) {
      console.error("Erreur lors de la recuperation des affectations vehicule", error);
    }
  };

  const addAffectationUser = (selectedOption: SingleValue<OptionType>) => {
    if (!selectedOption) return;

    setAffectations((prev) => {
      if (prev.some((affectation) => String(affectation.id_user) === String(selectedOption.value))) {
        return prev;
      }

      return [
        ...prev.filter((affectation) => affectation.id_user != null),
        {
          localId: `affectation-${selectedOption.value}-${Date.now()}`,
          id_user: selectedOption.value,
        },
      ];
    });
  };

  const removeAffectationUser = (userId: any) => {
    setAffectations((prev) => prev.filter((affectation) => String(affectation.id_user) !== String(userId)));
  };

  const handleCategoryChange = async (selectedOption: OptionType | null) => {
    if (!selectedOption) return;

    try {
      const res = await fetch(`${backendUrl}/api/vehicle/type-options/${selectedOption.value}`, { mode: "cors" });
      if (!res.ok) return console.error("Erreur lors de la récupération des types");

      const typesData = await res.json();
      const typesOptionsData = typesData.model.split(",").map((type: any) => ({
        value: type,
        label: type || "",
      }));

      setTypesOptions(typesOptionsData);

      setVehicle((prev) => ({
        ...prev,
        category_vehicule: selectedOption.value,
        vehicule_type: "",
      }));
    } catch (error) {
      console.error("Erreur lors de la récupération des types", error);
    }
  };

  const handleTypeChange = (selectedOption: OptionType | null) => {
    setVehicle((prev) => ({ ...prev, vehicule_type: selectedOption ? selectedOption.value : "" }));
  };

  const cancelClicked = () => navigate("/Vehicles");

  // ===============================
  // Validation (on ajoute VIN/Marque/Référence)
  // ===============================
  const validateNotEmpty = (v: string) => (v ?? "").trim() !== "";

  const setBorder = (id: string, ok: boolean) => {
    const el = document.getElementById(id) as HTMLElement | null;
    if (!el) return;
    (el as any).style.borderColor = ok ? "#ced4da" : "red";
    (el as any).style.border = ok ? "" : "1px solid red";
  };

  const saveVehicleUserAffectations = async (vehicleId: number) => {
    const cleanAffectations = affectations
      .filter((affectation) => affectation.id_user != null)
      .map((affectation, index) => ({
        id_user: affectation.id_user,
        is_primary: index === 0,
      }));

    const res = await fetch(`${backendUrl}/api/vehicle-affectations/${vehicleId}`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      mode: "cors",
      body: JSON.stringify({
        affectations: cleanAffectations,
        created_by: creatorUserId,
      }),
    });

    if (!res.ok) {
      throw new Error("vehicle affectations save failed");
    }
  };

  // ===============================
  // APIs: mêmes endpoints, payload enrichi
  // ===============================
  const updateVehicle = async (vehicle: VehicleInterface) => {
    const okImm = validateNotEmpty(vehicle.immatriculation_vehicule);
    const okDoor = validateNotEmpty(vehicle.num_porte_vehicule);
    const okVin = pageMode === "simple" || validateNotEmpty(vehicle.num_chassis_vehicule);
    const okBrand = pageMode === "simple" || validateNotEmpty(vehicle.modele_vehicule);
    const okRef = pageMode === "simple" || validateNotEmpty(vehicle.propriete_vehicule || "");
    const okUser = vehicle.id_user != null;
    const okGroup = vehicle.id_groupe != null;

    if (!okImm || !okDoor || !okVin || !okBrand || !okRef || !okUser || !okGroup) {
      setBorder("immatriculation_vehicule", okImm);
      setBorder("num_porte_vehicule", okDoor);
      setBorder("num_chassis_vehicule", okVin);
      setBorder("modele_vehicule", okBrand);
      setBorder("propriete_vehicule", okRef);
      setBorder("user", okUser);
      setBorder("group", okGroup);

      toast.warn(translate("Please fill in all required fields"), {
        position: "bottom-right",
        autoClose: 2400,
        transition: Bounce,
      });
      setButtonClicked(false);
      return;
    }

    const vehicleData = {
      // anciens champs (inchangés)
      category_vehicule: vehicle.category_vehicule,
      couleur_vehicule: vehicle.couleur_vehicule,
      id_conducteur_vehicule: vehicle.id_conducteur,
      id_groupe: vehicle.id_groupe,
      id_user: vehicle.id_user,
      immatriculation_vehicule: vehicle.immatriculation_vehicule,
      num_porte_vehicule: vehicle.num_porte_vehicule,
      vehicule_type: vehicle.vehicule_type,
      id_vehicule: vehicle.id_vehicule,
      GPSDIST: vehicle.GPSDIST,
      PSN: vehicle.PSN,

      // ✅ intégration demandée
      num_chassis_vehicule: vehicle.num_chassis_vehicule,
      modele_vehicule: vehicle.modele_vehicule,
      propriete_vehicule: vehicle.propriete_vehicule,
      ...getAdvancedVehicleData(vehicle),
      date_aquisition_vehicule: acquisitionDateTime
        ? acquisitionDateTime.toISOString().slice(0, 19).replace("T", " ")
        : null,
    };

    try {
      const recheck = await fetch(`${backendUrl}/api/vehicle/check`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        mode: "cors",
        body: JSON.stringify({
          immatriculation_vehicule: vehicle.immatriculation_vehicule,
          updatedImmatriculation: updateMatriculation,
          updated: updateMatriculation === vehicle.immatriculation_vehicule ? 0 : 1,
        }),
      });

      if (!recheck.ok) throw new Error("recheck failed");

      const jsonResponse = await recheck.json();
      if (jsonResponse.vehicle_count != 0) {
        toast.warn(translate("Vehicle immatriculation already exists ?"), {
          position: "bottom-right",
          autoClose: 2400,
          transition: Bounce,
        });
        setButtonClicked(false);
        return;
      }

      const res = await fetch(`${backendUrl}/api/vehicle/update`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        mode: "cors",
        body: JSON.stringify(vehicleData),
      });

      if (!res.ok) {
        toast.warn(translate("Can't updating vehicle"), {
          position: "bottom-right",
          autoClose: 2400,
          transition: Bounce,
        });
        setButtonClicked(false);
        return;
      }

      await saveVehicleUserAffectations(vehicle.id_vehicule);

      toast.success(translate("Vehicle updated successfully"), {
        position: "bottom-right",
        autoClose: 2400,
        transition: Bounce,
      });

      setButtonClicked(false);
      navigate("/Vehicles");
    } catch (error) {
      toast.warn(translate("Can't updating vehicle"), {
        position: "bottom-right",
        autoClose: 2400,
        transition: Bounce,
      });
      setButtonClicked(false);
    }
  };

  const createVehicle = async (vehicle: VehicleInterface) => {
    const okImm = validateNotEmpty(vehicle.immatriculation_vehicule);
    const okDoor = validateNotEmpty(vehicle.num_porte_vehicule);
    const okVin = pageMode === "simple" || validateNotEmpty(vehicle.num_chassis_vehicule);
    const okBrand = pageMode === "simple" || validateNotEmpty(vehicle.modele_vehicule);
    const okRef = pageMode === "simple" || validateNotEmpty(vehicle.propriete_vehicule || "");
    const okUser = vehicle.id_user != null;
    const okGroup = vehicle.id_groupe != null;

    if (!okImm || !okDoor || !okVin || !okBrand || !okRef || !okUser || !okGroup) {
      setBorder("immatriculation_vehicule", okImm);
      setBorder("num_porte_vehicule", okDoor);
      setBorder("num_chassis_vehicule", okVin);
      setBorder("modele_vehicule", okBrand);
      setBorder("propriete_vehicule", okRef);
      setBorder("user", okUser);
      setBorder("group", okGroup);

      toast.warn(translate("Please fill in all required fields"), {
        position: "bottom-right",
        autoClose: 2400,
        transition: Bounce,
      });
      setButtonClicked(false);
      return;
    }

    try {
      const vehicleData = {
        id_vehicule: vehicle.id_vehicule,
        category_vehicule: vehicle.category_vehicule,
        couleur_vehicule: vehicle.couleur_vehicule,
        id_conducteur_vehicule: vehicle.id_conducteur,
        id_groupe: vehicle.id_groupe,
        id_user: vehicle.id_user,
        immatriculation_vehicule: vehicle.immatriculation_vehicule,
        num_porte_vehicule: vehicle.num_porte_vehicule,
        vehicule_type: vehicle.vehicule_type,
        GPSDIST: vehicle.GPSDIST,
        PSN: vehicle.PSN,

        // ✅ intégration demandée
        num_chassis_vehicule: vehicle.num_chassis_vehicule,
        modele_vehicule: vehicle.modele_vehicule,
        propriete_vehicule: vehicle.propriete_vehicule,
        ...getAdvancedVehicleData(vehicle),
        date_aquisition_vehicule: acquisitionDateTime
          ? acquisitionDateTime.toISOString().slice(0, 19).replace("T", " ")
          : null,
      };

      const recheck = await fetch(`${backendUrl}/api/vehicle/check`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        mode: "cors",
        body: JSON.stringify({
          immatriculation_vehicule: vehicle.immatriculation_vehicule,
          updatedImmatriculation: updateMatriculation,
          update: 0,
        }),
      });

      if (!recheck.ok) throw new Error("recheck failed");

      const jsonResponse = await recheck.json();
      if (jsonResponse.vehicle_count != 0) {
        toast.warn(translate("Vehicle immatriculation already exists ?"), {
          position: "bottom-right",
          autoClose: 2400,
          transition: Bounce,
        });
        setButtonClicked(false);
        return;
      }

      const res = await fetch(`${backendUrl}/api/vehicle/add`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        mode: "cors",
        body: JSON.stringify(vehicleData),
      });

      if (!res.ok) {
        toast.warn(translate("Can't create a vehicle"), {
          position: "bottom-right",
          autoClose: 2400,
          transition: Bounce,
        });
        setButtonClicked(false);
        return;
      }

      const createdVehicle = await res.json();
      const createdVehicleId = Number(createdVehicle.id_vehicle || createdVehicle.id_vehicule || createdVehicle.id_vehicle);
      if (createdVehicleId) {
        await saveVehicleUserAffectations(createdVehicleId);
      }

      toast.success(translate("Vehicle created successfully !"), {
        position: "bottom-right",
        autoClose: 2400,
        transition: Bounce,
      });

      navigate("/Vehicles");
      setButtonClicked(false);
    } catch (error) {
      toast.warn(translate("Can't create a vehicle"), {
        position: "bottom-right",
        autoClose: 2400,
        transition: Bounce,
      });
      setButtonClicked(false);
    }
  };

  // ===============================
  // SelectGroup helpers
  // ===============================
  const categoryOptions = [
    { value: "Aucun", label: "Aucun" },
    { value: "PL", label: "PL" },
    { value: "VL", label: "VL" },
    { value: "Engin", label: "Engin" },
    { value: "Outils", label: "Outils" },
    { value: "Utilitaires", label: "Utilitaires" },
    { value: "Autre", label: "Autre" },
  ];

  const findUserById = (id: number) => {
    const u = Users.find((x) => x.id_user === id);
    return u ? { value: u.id_user, label: `${u.nom_user} ${u.prenom_user || ""}` } : undefined;
  };
  const findDriverById = (id: number) => {
    const d = Drivers.find((x) => x.id_conducteur === id);
    return d ? { value: d.id_conducteur, label: `${d.nom_conducteur} ${d.prenom_conducteur || ""}` } : undefined;
  };
  const findGroupById = (id: number) => {
    const g = groups.find((x) => x.id_groupe === id);
    return g ? { value: g.id_groupe, label: `${g.nom_groupe || ""}` } : undefined;
  };

  const vehicleSheetName = translate("Fiche véhicule");
  const vehicleExportHeaders = [
    translate("Immatriculation Véhicule"),
    translate("N° porte"),
    translate("Date d'acquisition"),
    translate("N° châssis (VIN)"),
    translate("Odomètre"),
    translate("Catégorie Véhicule"),
    translate("Type véhicule"),
    translate("Marque"),
    translate("Référence"),
    translate("Utilisateur"),
    translate("Chauffeur"),
    translate("Groupe"),
    translate("Dispositif (PSN)"),
    translate("Couleur"),
  ];
  const getVehicleExportRows = () => [[
    safeText(Vehicle.immatriculation_vehicule),
    safeText(Vehicle.num_porte_vehicule),
    acquisitionDateTime ? acquisitionDateTime.toLocaleString("fr-FR") : "—",
    safeText(Vehicle.num_chassis_vehicule),
    safeText(Vehicle.GPSDIST),
    safeText(Vehicle.category_vehicule),
    safeText(Vehicle.vehicule_type),
    safeText(Vehicle.modele_vehicule),
    safeText(Vehicle.propriete_vehicule),
    safeText(findUserById(Vehicle.id_user || 0)?.label),
    safeText(findDriverById(Vehicle.id_conducteur || 0)?.label),
    safeText(findGroupById(Vehicle.id_groupe || 0)?.label),
    safeText(Vehicle.PSN),
    safeText(Vehicle.couleur_vehicule),
  ]];

  const exportVehicleExcel = () => {
    generateExcelFile(vehicleSheetName, vehicleExportHeaders, getVehicleExportRows());
  };

  // ===============================
  // Sidebar recap + completion %
  // ===============================
  const safeText = (v: any) => {
    const s = String(v ?? "").trim();
    return s === "" ? "—" : s;
  };

  const completion = useMemo(() => {
    const isFilled = (v: any) => {
      if (v === null || v === undefined) return false;
      if (typeof v === "number") return !Number.isNaN(v); // 0 = OK
      const s = String(v).trim();
      if (s === "" || s === "—") return false;
      if (s.toLowerCase() === "aucun") return false;
      return true;
    };

    // ✅ Règles EXACTES (et on stocke aussi la valeur lue)
    const rules = [
      { key: "immatriculation", label: "Immatriculation", ok: isFilled(Vehicle.immatriculation_vehicule), value: Vehicle.immatriculation_vehicule },
      { key: "porte", label: "N° porte", ok: isFilled(Vehicle.num_porte_vehicule), value: Vehicle.num_porte_vehicule },
      { key: "vin", label: "VIN", ok: isFilled(Vehicle.num_chassis_vehicule), value: Vehicle.num_chassis_vehicule },

      { key: "categorie", label: "Catégorie", ok: isFilled(Vehicle.category_vehicule), value: Vehicle.category_vehicule },
      { key: "type", label: "Type", ok: isFilled(Vehicle.vehicule_type), value: Vehicle.vehicule_type },
      { key: "marque", label: "Marque", ok: isFilled(Vehicle.modele_vehicule), value: Vehicle.modele_vehicule },
      { key: "reference", label: "Référence", ok: isFilled(Vehicle.propriete_vehicule), value: Vehicle.propriete_vehicule },

      { key: "user", label: "Utilisateur", ok: isFilled(Vehicle.id_user), value: Vehicle.id_user },
      { key: "group", label: "Groupe", ok: isFilled(Vehicle.id_groupe), value: Vehicle.id_groupe },

      { key: "device", label: "Dispositif (PSN)", ok: isFilled(Vehicle.PSN), value: Vehicle.PSN },

      // Couleur: tu peux la retirer si tu ne veux pas la compter
      { key: "color", label: "Couleur", ok: isFilled(Vehicle.couleur_vehicule), value: Vehicle.couleur_vehicule },
    ];

    const total = rules.length;
    const done = rules.filter(r => r.ok).length;
    const percent = total ? Math.round((done / total) * 100) : 0;

    const missing = rules.filter(r => !r.ok).map(r => r.label);

    const status = percent === 100 ? "complete" : percent >= 70 ? "almost" : "incomplete";

    return { total, done, percent, status, missing, rules };
  }, [Vehicle]);


  const progressVariant = completion.status === "complete" ? "success" : completion.status === "almost" ? "primary" : "warning";

  const statusChip = () => {
    if (completion.status === "complete") {
      return <span className="veh-chip veh-chip-ok"><i className="bi bi-check-circle" /> {translate("Complet")}</span>;
    }
    if (completion.status === "almost") {
      return <span className="veh-chip veh-chip-info"><i className="bi bi-info-circle" /> {translate("Presque")}</span>;
    }
    return <span className="veh-chip veh-chip-warn"><i className="bi bi-exclamation-circle" /> {translate("Incomplet")}</span>;
  };

  const VehicleSelectGroup = pageMode === "simple" ? SimpleSelectGroup : AdvancedSelectGroup;
  const fuelOptions: OptionType[] = [
    { value: "", label: translate("Sélectionnez une option") },
    { value: "Diesel", label: "Diesel" },
    { value: "Essence", label: translate("Essence") },
    { value: "Hybride", label: translate("Hybride") },
    { value: "Electrique", label: translate("Électrique") },
    { value: "GPL", label: "GPL" },
  ];
  const vehicleStatusOptions: OptionType[] = [
    { value: "", label: translate("Sélectionnez une option") },
    { value: "Actif", label: translate("Actif") },
    { value: "Inactif", label: translate("Inactif") },
    { value: "Maintenance", label: translate("Maintenance") },
    { value: "Hors service", label: translate("Hors service") },
  ];
  const yesNoOptions: OptionType[] = [
    { value: "", label: translate("Sélectionnez une option") },
    { value: "1", label: translate("Oui") },
    { value: "0", label: translate("Non") },
  ];

  const technicalFields: VehicleExtraField[] = [
    { name: "annee_vehicule", label: "Année", placeholder: "Ex: 2026", type: "number" },
    { name: "date_circulation_vehicule", label: "Date de circulation", type: "date" },
    { name: "nbre_place_vehicule", label: "Nombre de places", type: "number" },
    { name: "nbre_porte_vehicule", label: "Nombre de portes", type: "number" },
    { name: "puissance_vehicule", label: "Puissance", type: "number", unit: "CV" },
    { name: "type_carburant_vehicule", label: "Type carburant", options: fuelOptions },
    { name: "fuel_type", label: "Fuel type", options: fuelOptions },
    { name: "etat_vehicule", label: "État véhicule", options: vehicleStatusOptions },
    { name: "famille_vehicule", label: "Famille" },
    { name: "gamme_vehicule", label: "Gamme" },
    { name: "longueur_vehicule", label: "Longueur", type: "number", step: "0.01", unit: "m" },
    { name: "largeur_vehicule", label: "Largeur", type: "number", step: "0.01", unit: "m" },
    { name: "hauteur_vehicule", label: "Hauteur", type: "number", step: "0.01", unit: "m" },
    { name: "poid_vehicule", label: "Poids", type: "number", step: "0.01", unit: "kg" },
    { name: "ptac_vehicule", label: "PTAC", type: "number", step: "0.01", unit: "kg" },
    { name: "maximum_allowed_total", label: "Poids maximal autorisé", type: "number", step: "0.01", unit: "kg" },
    { name: "consommation_moyenne_vehicule", label: "Consommation moyenne", type: "number", step: "0.01", unit: "L/100km" },
    { name: "fuel_level_vehicule", label: "Niveau carburant", type: "number", step: "0.01", unit: "%" },
    { name: "co2_vehicule", label: "CO2", type: "number", step: "0.01", unit: "g/km" },
    { name: "capacite_res_vehicule", label: "Capacité réservoir", type: "number", step: "0.01", unit: "L" },
  ];

  const insuranceFields: VehicleExtraField[] = [
    { name: "companie_assurance_vehicule", label: "Compagnie assurance" },
    { name: "type_assurance_vehicule", label: "Type assurance" },
    { name: "date_debut_assurance_vehicule", label: "Début assurance", type: "date" },
    { name: "date_expir_assurance_vehicule", label: "Expiration assurance", type: "date" },
    { name: "cout_assurance_vehicule", label: "Coût assurance", type: "number", step: "0.01", unit: "DA" },
    { name: "delai_assurance_vehicule", label: "Délai assurance", type: "number", unit: "jours" },
    { name: "reference_assurance_vehicule", label: "Référence assurance" },
    { name: "note_assurance_vehicule", label: "Note assurance" },
  ];

  const controlFields: VehicleExtraField[] = [
    { name: "etat_ctr_tech_vehicule", label: "État contrôle technique", options: vehicleStatusOptions },
    { name: "date_debut_ctr_tech_vehicule", label: "Début contrôle technique", type: "date" },
    { name: "date_fin_ctr_tech_vehicule", label: "Fin contrôle technique", type: "date" },
    { name: "station_ctr_vehicule", label: "Station contrôle" },
    { name: "cout_ctr_tech_vehicule", label: "Coût contrôle technique", type: "number", step: "0.01", unit: "DA" },
    { name: "reference_ctr_tech_vehicule", label: "Référence contrôle technique" },
    { name: "note_ctr_tech_vehicule", label: "Note contrôle technique" },
    { name: "date_vignette_vehicule", label: "Date vignette", type: "date" },
    { name: "cout_vignette_vehicule", label: "Coût vignette", type: "number", step: "0.01", unit: "DA" },
    { name: "num_vignette_vehicule", label: "N° vignette" },
  ];

  const maintenanceFields: VehicleExtraField[] = [
    { name: "kilometrage_reel_vehicule", label: "Kilométrage réel", type: "number", unit: "km" },
    { name: "dernier_vidange_vehicule", label: "Dernière vidange", type: "number", unit: "km" },
    { name: "date_dernier_vidange", label: "Date dernière vidange", type: "date" },
    { name: "prochain_vidange_vehicule", label: "Prochaine vidange", type: "number", unit: "km" },
    { name: "kilometrage_prochain_entretien", label: "Kilométrage prochain entretien", type: "number", unit: "km" },
    { name: "inService_vehicule", label: "En service", options: yesNoOptions },
    { name: "detail_vehicule", label: "Détail" },
    { name: "info_vehicule", label: "Info véhicule", type: "number" },
    { name: "commentaire_vehicule", label: "Commentaire" },
  ];

  const normalizeAdvancedFieldValue = (field: VehicleExtraField) => {
    const value = Vehicle[field.name] as any;
    if (value === null || value === undefined) return "";
    const textValue = String(value);

    if (field.type === "date") {
      return /^\d{4}-\d{2}-\d{2}/.test(textValue) ? textValue.slice(0, 10) : "";
    }

    if (field.type === "datetime-local") {
      if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/.test(textValue)) return textValue.slice(0, 16);
      if (/^\d{4}-\d{2}-\d{2}\s\d{2}:\d{2}/.test(textValue)) return textValue.replace(" ", "T").slice(0, 16);
      return "";
    }

    return textValue;
  };

  const renderAdvancedField = (field: VehicleExtraField) => {
    const controlName = String(field.name);
    const value = normalizeAdvancedFieldValue(field);

    return (
      <Col md={4} key={controlName}>
        <Form.Group controlId={controlName} className="veh-advanced-field">
          <Form.Label>
            <strong>{translate(field.label)}</strong>
          </Form.Label>

          {field.options ? (
            <Form.Select name={controlName} value={value} onChange={handleChange}>
              {field.options.map((option) => (
                <option key={String(option.value)} value={option.value}>
                  {option.label}
                </option>
              ))}
            </Form.Select>
          ) : field.unit ? (
            <InputGroup>
              <Form.Control
                type={field.type || "text"}
                step={field.step}
                placeholder={translate(field.placeholder || field.label)}
                name={controlName}
                value={value}
                onChange={handleChange}
              />
              <InputGroup.Text>{field.unit}</InputGroup.Text>
            </InputGroup>
          ) : (
            <Form.Control
              type={field.type || "text"}
              step={field.step}
              placeholder={translate(field.placeholder || field.label)}
              name={controlName}
              value={value}
              onChange={handleChange}
            />
          )}
        </Form.Group>
      </Col>
    );
  };

  // ===============================
  // UI
  // ===============================
  return (
    <>
      <style>{`
      
        .veh-card { border: 1px solid rgba(0,0,0,.06); border-radius: 18px; box-shadow: 0 12px 30px rgba(18,38,63,.08); overflow:hidden; }
        .veh-section { padding: 18px 20px; border-bottom: 1px solid rgba(0,0,0,.06); }
        .veh-section:last-child { border-bottom: 0; }
        .veh-section-title { font-weight: 800; font-size: 16px; color: #1f2a37; margin-bottom: 5px; display:flex; align-items:center; gap:8px; }
        .veh-subsection-title { font-weight:800; font-size:14px; color:#334155; margin:10px 0 12px; padding-bottom:8px; border-bottom:1px dashed rgba(100,116,139,.25); }
        .veh-advanced-field .form-label { min-height:20px; margin-bottom:7px; }
        .veh-advanced-field .form-control,
        .veh-advanced-field .form-select,
        .veh-advanced-field .input-group-text { min-height:43px; }
        .veh-advanced-field .form-control,
        .veh-advanced-field .form-select { padding-left:14px; padding-right:14px; }
        .veh-advanced-field .input-group-text { min-width:54px; justify-content:center; font-weight:700; color:#475569; background:#f8fafc; }
        .veh-advanced-tabs { display:flex; flex-wrap:wrap; gap:8px; padding:14px 18px 6px; border-bottom:1px solid rgba(0,0,0,.06); background:#fff; }
        .veh-advanced-tab { border:1px solid #d7dee8; background:#fff; color:#334155; border-radius:8px; min-height:38px; padding:7px 12px; font-weight:700; font-size:13px; display:inline-flex; align-items:center; gap:7px; }
        .veh-advanced-tab:hover { border-color:#0d6efd; color:#0d6efd; }
        .veh-advanced-tab.active { border-color:#0d6efd; background:#0d6efd; color:#fff; }
        .veh-page.advanced .veh-advanced-tab-section:not(.is-active) { display:none; }
        .veh-actions { position: sticky; bottom: 0; z-index: 10; background: rgba(255,255,255,.92); backdrop-filter: blur(10px); border-top: 1px solid rgba(0,0,0,.06); padding: 12px 16px; }
        .veh-sidebar { position: sticky; top: 18px; }
        .veh-sidebar-card { border: 1px solid rgba(0,0,0,.06); border-radius: 18px; box-shadow:0 10px 22px rgba(18,38,63,.06); overflow:hidden; }
        .veh-sidebar-header { padding: 16px 16px 12px; border-bottom:1px solid rgba(0,0,0,.06); background: linear-gradient(180deg, #fff, rgba(255,255,255,.92)); }
        .veh-kv { padding: 12px 16px; }
        .veh-kv-row { display:flex; justify-content:space-between; gap:10px; padding: 10px 0; border-bottom: 1px dashed rgba(0,0,0,.12); }
        .veh-kv-row:last-child { border-bottom: 0; }
        .veh-k { color:#64748b; font-size: 12px; }
        .veh-v { font-weight: 800; color:#111827; font-size: 13px; text-align:right; }
        .veh-chip { display:inline-flex; align-items:center; gap:6px; padding:6px 10px; border-radius:999px; font-weight:800; font-size:12px; border:1px solid rgba(0,0,0,.10); background:#fff; white-space:nowrap; }
        .veh-chip-ok { border-color: rgba(25,135,84,.18); background: rgba(25,135,84,.08); color:#198754; }
        .veh-chip-warn { border-color: rgba(255,193,7,.25); background: rgba(255,193,7,.12); color:#8a6d00; }
        .veh-chip-info { border-color: rgba(13,110,253,.20); background: rgba(13,110,253,.10); color:#0d6efd; }
        .veh-dot { width:12px; height:12px; border-radius:999px; border:1px solid rgba(0,0,0,.15); display:inline-block; vertical-align:middle; }
        .veh-mode-button {
          min-width:150px;
          font-weight:700;
          background:#f59e0b !important;
          border-color:#f59e0b !important;
          color:#fff !important;
        }
        .veh-mode-button:hover,
        .veh-mode-button:focus,
        .veh-mode-button:active {
          background:#d97706 !important;
          border-color:#d97706 !important;
          color:#fff !important;
        }
        .veh-header-tools { width:auto; border:0; box-shadow:none; padding:0; background:transparent; display:flex; align-items:center; gap:10px; }
        .veh-header-tools .text-muted { margin:0 !important; white-space:nowrap; }
        .veh-header-tools .d-grid { display:flex !important; gap:8px !important; }
        .veh-header-tools .d-grid > button:nth-child(3) { display:none; }
        .veh-header-tools .btn { min-height:38px; font-size:14px; padding:7px 14px; white-space:nowrap; }
        .veh-header-right > div:last-child { order:-1; }
        .veh-sidebar-tools { display:none; }
        .veh-date-field .input-group {
          display:flex;
          flex-wrap:nowrap;
          align-items:stretch;
        }
        .veh-date-field .react-datepicker-wrapper,
        .veh-date-field .react-datepicker__input-container {
          flex:1 1 auto;
          min-width:0;
          display:block;
        }
        .veh-date-field .react-datepicker-wrapper .form-control {
          width:100% !important;
        }
        .veh-date-field .input-group-text {
          flex:0 0 43px;
          display:flex;
          align-items:center;
          justify-content:center;
        }
        .veh-affectation-row { border:1px solid rgba(0,0,0,.08); border-radius:8px; padding:14px; margin-bottom:14px; background:#fff; }
        .veh-affectation-row:last-child { margin-bottom:0; }
        .veh-tag-list { display:flex; flex-wrap:wrap; gap:8px; margin-top:14px; }
        .veh-user-tag { display:inline-flex; align-items:center; gap:7px; max-width:100%; border:1px solid rgba(13,110,253,.22); background:rgba(13,110,253,.08); color:#0b5ed7; border-radius:999px; padding:6px 8px 6px 10px; font-size:13px; font-weight:700; }
        .veh-user-tag span { min-width:0; overflow:hidden; text-overflow:ellipsis; white-space:nowrap; }
        .veh-user-tag button { width:22px; height:22px; border:0; border-radius:999px; display:inline-flex; align-items:center; justify-content:center; background:rgba(13,110,253,.13); color:#0b5ed7; padding:0; }
        .veh-user-tag button:hover { background:rgba(220,53,69,.14); color:#dc3545; }
        .veh-empty-tags { margin-top:12px; border:1px dashed rgba(100,116,139,.35); border-radius:8px; padding:12px; color:#64748b; font-size:13px; }
        .veh-sidebar-card + .veh-sidebar-card { margin-top:16px; }
        .veh-recap-card { display:block !important; }
        .veh-page-header { position:relative; min-height:48px; margin-bottom:18px; }
        .veh-header-shell { display:flex; align-items:flex-start; justify-content:space-between; gap:16px; }
        .veh-title-block { min-width:0; }
        .veh-header-right { position:absolute; top:0; right:0; z-index:20; }
        .veh-content-row { align-items:flex-start; }
        .veh-page.simple .advanced-only { display:none; }
        .veh-page.simple .veh-card { border:0; box-shadow:none; border-radius:0; overflow:visible; padding-left:56px; padding-right:34px; }
        .veh-page.simple .veh-section { padding:0; border-bottom:0; max-width:1120px; }
        .veh-page.simple .veh-section-title { display:none; }
        .veh-page.simple .veh-section .row.g-3 { display:block; }
        .veh-page.simple .veh-section .row.g-3 > [class*="col-"] { max-width:100%; width:100%; padding-left:0; padding-right:0; }
        .veh-page.simple .veh-section .form-group,
        .veh-page.simple .veh-section [class*="mb-3"] { margin-bottom:0; }
        .veh-page.simple .veh-section .form-label,
        .veh-page.simple .veh-section label { font-weight:400 !important; font-size:15px !important; line-height:1.25; color:#000; white-space:nowrap; }
        .veh-page.simple .veh-section strong { font-weight:700; }
        .veh-page.simple .veh-section .form-control,
        .veh-page.simple .veh-section .react-select__control { min-height:43px; border-color:#ced4da; box-shadow:none; }
        .veh-page.simple .veh-section input.form-control,
        .veh-page.simple .veh-section textarea.form-control,
        .veh-page.simple .veh-section select.form-control,
        .veh-page.simple .veh-section .form-control {
          padding-left:24px !important;
          padding-right:24px !important;
          text-indent:0 !important;
        }
        .veh-page.simple .veh-section .input-group > .form-control,
        .veh-page.simple .veh-section .react-datepicker-wrapper .form-control {
          padding-left:24px !important;
          padding-right:18px !important;
        }
        .veh-page.simple .veh-section .react-select__value-container {
          padding-left:24px !important;
          padding-right:16px !important;
        }
        .veh-page.simple .veh-section .react-select__input-container,
        .veh-page.simple .veh-section .react-select__placeholder,
        .veh-page.simple .veh-section .react-select__single-value {
          margin-left:0 !important;
          padding-left:0 !important;
        }
        .veh-page.simple .veh-section .react-select__input,
        .veh-page.simple .veh-section .react-select__input input {
          padding-left:0 !important;
          margin-left:0 !important;
        }
        .veh-page .react-datepicker-popper {
          z-index: 80 !important;
        }
        .veh-page .react-datepicker {
          display:inline-flex !important;
          align-items:stretch;
          border-color:#ced4da;
          box-shadow:0 10px 24px rgba(18,38,63,.14);
          font-family:inherit;
          overflow:hidden;
        }
        .veh-page .react-datepicker__month-container {
          float:none;
          width:228px;
        }
        .veh-page .react-datepicker__header {
          padding-top:10px;
          background:#f8f9fa;
        }
        .veh-page .react-datepicker__current-month {
          font-size:14px;
          margin-bottom:6px;
        }
        .veh-page .react-datepicker__day-name,
        .veh-page .react-datepicker__day {
          width:24px;
          line-height:24px;
          margin:2px 4px;
        }
        .veh-page .react-datepicker__time-container {
          float:none;
          width:76px;
          border-left:1px solid #ced4da;
        }
        .veh-page .react-datepicker__time-container .react-datepicker__time {
          height:210px;
        }
        .veh-page .react-datepicker__time-container .react-datepicker__time-box {
          width:76px;
        }
        .veh-page .react-datepicker__time-container .react-datepicker__time-box ul.react-datepicker__time-list {
          height:210px !important;
        }
        .veh-page .react-datepicker__time-container .react-datepicker__time-box ul.react-datepicker__time-list li.react-datepicker__time-list-item {
          height:28px;
          padding:5px 6px;
        }
        .veh-page .react-datepicker__navigation {
          top:8px;
        }
        .veh-page.simple .veh-section .form-control,
        .veh-page.simple .veh-section .input-group,
        .veh-page.simple .veh-section .react-select__container,
        .veh-page.simple .veh-section .react-select__control,
        .veh-page.simple .veh-section .css-b62m3t-container { width:100%; max-width:100%; }
        .veh-page.simple .veh-section .row.g-3 > div > .form-group > *:not(.form-label),
        .veh-page.simple .veh-section .row.g-3 > div > div:not(.input-group) > *:not(.form-label) { width:100%; max-width:100%; }
        .veh-page.simple .veh-section input[type="color"].form-control { width:70px !important; min-width:70px; max-width:70px !important; padding:6px; }
        .veh-page.simple .veh-date-field .input-group { display:flex; flex-wrap:nowrap; max-width:280px !important; }
        .veh-page.simple .veh-date-field .react-datepicker-wrapper,
        .veh-page.simple .veh-date-field .react-datepicker__input-container { flex:1 1 auto; min-width:0; display:block; }
        .veh-page.simple .veh-date-field .react-datepicker-wrapper .form-control { width:100% !important; }
        .veh-page.simple .veh-date-field .input-group-text { flex:0 0 43px; min-height:43px; justify-content:center; }
        .veh-page.simple .veh-color-field .d-flex { width:auto !important; max-width:360px !important; gap:16px !important; }
        .veh-page.simple .veh-color-field input[type="text"].form-control { width:160px !important; max-width:160px !important; }
        .veh-page.simple .veh-section .form-group,
        .veh-page.simple .veh-section [controlid] { margin-top:0; }
        .veh-page.simple .veh-section .row.g-3 > div > .form-group,
        .veh-page.simple .veh-section .row.g-3 > div > .row,
        .veh-page.simple .veh-section .row.g-3 > div > div:not(.input-group) {
          margin-top:22px !important;
        }
        .veh-page.simple .veh-section + .veh-section { margin-top:8px; }
        .veh-page.simple .veh-section .row.g-3 > .veh-simple-space-fix > .form-group,
        .veh-page.simple .veh-section .row.g-3 > .veh-simple-space-fix > .row,
        .veh-page.simple .veh-section .row.g-3 > .veh-simple-space-fix > div:not(.input-group) {
          margin-top:32px !important;
        }
        .veh-page.simple .veh-actions { position:static; background:#fff; border-top:1px solid #dee2e6; margin:24px 0 0; padding:8px 0 0; }
        @media (min-width: 768px) {
          .veh-page.simple .veh-section .row.g-3 > div > .form-group,
          .veh-page.simple .veh-section .row.g-3 > div > .row,
          .veh-page.simple .veh-section .row.g-3 > div > div:not(.input-group) {
            display:grid;
            grid-template-columns: 260px minmax(0, 1fr);
            align-items:center;
            column-gap:34px;
          }
          .veh-page.simple .veh-section .row.g-3 > div > .row {
            margin-left:0;
            margin-right:0;
          }
          .veh-page.simple .veh-section .row.g-3 > div > .form-group > *:not(.form-label),
          .veh-page.simple .veh-section .row.g-3 > div > div:not(.input-group) > *:not(.form-label) {
            width:100%;
            max-width:780px;
          }
          .veh-page.simple .veh-section .row.g-3 > div > .row > [class*="col-"] {
            width:100%;
            max-width:780px;
            padding-left:0 !important;
            padding-right:0 !important;
          }
          .veh-page.simple .veh-section .row.g-3 > div > .form-group > .form-label,
          .veh-page.simple .veh-section .row.g-3 > div > .row > .form-label,
          .veh-page.simple .veh-section .row.g-3 > div > div > .form-label {
            width:auto;
            max-width:none;
            padding:0;
            margin-bottom:0;
            text-align:left !important;
          }
        }
        @media (min-width: 992px) {
          .veh-content-row > .col-lg-4 { padding-top:0; }
        }
        @media (max-width: 991.98px) {
          .veh-page-header { min-height:0; }
          .veh-header-shell {
            display:grid !important;
            grid-template-columns:1fr;
            flex-direction:column;
            align-items:stretch !important;
            gap:14px;
            justify-content:initial !important;
          }
          .veh-page-header h3 {
            font-size:24px;
            line-height:1.15;
          }
          .veh-title-block {
            width:100%;
          }
          .veh-title-block > .d-flex {
            align-items:center;
            flex-wrap:wrap;
          }
          .veh-header-right {
            position:static;
            width:100%;
            margin-top:0;
            flex-direction:column !important;
            align-items:stretch !important;
            justify-content:flex-start !important;
          }
          .veh-header-right > div {
            width:100%;
          }
          .veh-header-right > div:last-child > .d-flex {
            width:100%;
          }
          .veh-header-tools { width:100%; }
          .veh-header-tools,
          .veh-header-tools .d-grid { flex-wrap:wrap; }
          .veh-header-tools .d-grid {
            display:grid !important;
            grid-template-columns: repeat(2, minmax(0, 1fr));
            width:100%;
          }
          .veh-header-tools .btn,
          .veh-mode-button {
            width:100%;
            min-width:0;
          }
          .veh-header-tools .btn {
            padding-left:10px;
            padding-right:10px;
          }
          .veh-sidebar {
            position:static;
          }
          .veh-page.simple .veh-card { padding-left:16px; padding-right:16px; }
        }
        @media (max-width: 420px) {
          .veh-header-tools .d-grid {
            grid-template-columns: 1fr;
          }
        }
      `}</style>

      <div className={`veh-page ${pageMode}`}>
        {/* Header */}
        <div className="row veh-page-header">
          <div className="col-12 veh-header-shell">
            <div className="veh-title-block">
              <div className="d-flex align-items-center gap-2">
                <i className="bi bi-sliders text-primary fs-4" />
                <h3 className="m-0 fw-bold">
                  {id_vehicule ? translate("Edit vehicle") : translate("New vehicle")}
                </h3>
                <Badge bg="light" text="dark" className="border">
                  {translate("Tool")}
                </Badge>
              </div>
              <div className="text-muted" style={{ fontSize: 13 }}>
                {translate("Configuration only (no tracking metrics)")}
              </div>
            </div>

            <div className="veh-header-right d-flex flex-row flex-wrap gap-2 align-items-center justify-content-end">
              <div className="veh-header-tools">
                <div className="d-grid gap-2">
                  <Button variant="outline-primary" type="button" onClick={exportVehicleExcel}>
                    <i className="bi bi-download" /> {translate("Exporter fiche")}
                  </Button>

                  <Button variant="outline-secondary" type="button" onClick={() => window.print()}>
                    <i className="bi bi-printer" /> {translate("Imprimer")}
                  </Button>

                  <Button
                    variant="outline-secondary"
                    type="button"
                    onClick={async () => {
                      try {
                        await navigator.clipboard.writeText(Vehicle.propriete_vehicule || "");
                        toast.success(translate("Copied"), { position: "bottom-right", autoClose: 1400, transition: Bounce });
                      } catch {
                        toast.warn(translate("Copy failed"), { position: "bottom-right", autoClose: 1400, transition: Bounce });
                      }
                    }}
                  >
                    <i className="bi bi-clipboard" /> {translate("Copier référence")}
                  </Button>
                </div>
              </div>

              <div className="d-flex flex-wrap gap-2 align-items-center justify-content-end">
                <Button
                  className="veh-mode-button"
                  variant="primary"
                  type="button"
                  onClick={() => setPageMode((mode) => (mode === "simple" ? "advanced" : "simple"))}
                >
                  <i className={pageMode === "simple" ? "bi bi-sliders" : "bi bi-list-check"} />{" "}
                  {pageMode === "simple" ? translate("Advanced mode") : translate("Simple mode")}
                </Button>
              </div>
            </div>
          </div>
        </div>

        <div className="row g-4 veh-content-row">
          {/* LEFT: Form */}
          <div className="col-lg-8">
            <Card className="veh-card">
              <Form
                noValidate
                onSubmit={(e) => {
                  e.preventDefault();
                }}
              >
                <div className="veh-advanced-tabs advanced-only">
                  <button
                    type="button"
                    className={`veh-advanced-tab ${activeAdvancedTab === "identity" ? "active" : ""}`}
                    onClick={() => setActiveAdvancedTab("identity")}
                  >
                    <i className="las la-car" /> {translate("Identité")}
                  </button>
                  <button
                    type="button"
                    className={`veh-advanced-tab ${activeAdvancedTab === "classification" ? "active" : ""}`}
                    onClick={() => setActiveAdvancedTab("classification")}
                  >
                    <i className="las la-tags" /> {translate("Classification")}
                  </button>
                  <button
                    type="button"
                    className={`veh-advanced-tab ${activeAdvancedTab === "appearance" ? "active" : ""}`}
                    onClick={() => setActiveAdvancedTab("appearance")}
                  >
                    <i className="las la-palette" /> {translate("Apparence")}
                  </button>
                  <button
                    type="button"
                    className={`veh-advanced-tab ${activeAdvancedTab === "assignment" ? "active" : ""}`}
                    onClick={() => setActiveAdvancedTab("assignment")}
                  >
                    <i className="las la-id-card" /> {translate("Affectations")}
                  </button>
                  <button
                    type="button"
                    className={`veh-advanced-tab ${activeAdvancedTab === "technical" ? "active" : ""}`}
                    onClick={() => setActiveAdvancedTab("technical")}
                  >
                    <i className="las la-tools" /> {translate("Technique")}
                  </button>
                  <button
                    type="button"
                    className={`veh-advanced-tab ${activeAdvancedTab === "documents" ? "active" : ""}`}
                    onClick={() => setActiveAdvancedTab("documents")}
                  >
                    <i className="las la-file-contract" /> {translate("Documents")}
                  </button>
                  <button
                    type="button"
                    className={`veh-advanced-tab ${activeAdvancedTab === "maintenance" ? "active" : ""}`}
                    onClick={() => setActiveAdvancedTab("maintenance")}
                  >
                    <i className="las la-oil-can" /> {translate("Entretien")}
                  </button>
                </div>

                {/* INFORMATIONS */}
                <div className={`veh-section veh-advanced-tab-section ${activeAdvancedTab === "identity" ? "is-active" : ""}`}>
                  <div className="veh-section-title">
                    <i className="las la-car text-primary" /> {translate("Informations")}
                  </div>

                  <Row className="g-3">
                    <Col md={6}>
                      <Form.Group controlId="immatriculation_vehicule"  style={{ fontSize: "14px" }}>
                        <Form.Label>
                         <strong> {translate("Matriculation")}  {translate("Vehicle")}</strong> <span className="text-danger">*</span>
                        </Form.Label>
                        <Form.Control
                          type="text"
                          placeholder="Saisir l'immatriculation du véhicule"
                          name="immatriculation_vehicule"
                          value={Vehicle.immatriculation_vehicule}
                          onChange={handleChange}
                          required
                        />
                      </Form.Group>
                    </Col>

                    <Col md={3}>
                      <Form.Group controlId="num_porte_vehicule" style={{ fontSize: "14px" }}>
                        <Form.Label>
                          <strong>  {translate("Door number")} </strong> <span className="text-danger">*</span>
                        </Form.Label>
                        <Form.Control
                          type="text"
                          placeholder="Saisir le numéro porte"
                          name="num_porte_vehicule"
                          value={Vehicle.num_porte_vehicule}
                          onChange={handleChange}
                          required
                        />
                      </Form.Group>
                    </Col>

                    <Col md={3}>
                      <Form.Group controlId="acquisitionDateTime" className="veh-date-field advanced-only" style={{ fontSize: "14px" }}>
                        <Form.Label> <strong>{translate("Acquisition date")} </strong></Form.Label>
                        <div className="input-group">
                          <DatePicker
                            selected={acquisitionDateTime}
                            onChange={(date) => setAcquisitionDateTime(date as Date)}
                            showTimeSelect
                            timeFormat="HH:mm"
                            timeIntervals={15}
                            timeCaption="Time"
                            dateFormat="MM/dd/yyyy h:mm aa"
                            customInput={<Form.Control type="text" placeholder="Select Date and Time" />}
                          />
                          <span className="input-group-text">
                            <i className="las la-calendar" />
                          </span>
                        </div>
                      </Form.Group>
                    </Col>

                    <Col md={6}>
                      <Form.Group controlId="num_chassis_vehicule" className="advanced-only" style={{ fontSize: "14px" }}>
                        <Form.Label>
                          <strong> {translate("N° châssis (VIN)")} </strong><span className="text-danger">*</span>
                        </Form.Label>
                        <Form.Control
                          type="text"
                          placeholder="Ex: VF1XXXXXXXXXXXXXX"
                          name="num_chassis_vehicule"
                          value={Vehicle.num_chassis_vehicule}
                          onChange={handleChange}
                          required
                        />
                      </Form.Group>
                    </Col>

                    <Col md={6}>
                      <Form.Group controlId="GPSDIST" style={{ fontSize: "14px" }}>
                        <Form.Label>
                            <strong>{translate("Odometer")}</strong><span className="text-danger">*</span>
                        </Form.Label>
                        <Form.Control
                          type="number"
                          placeholder={translate("Enter the Odometer")}
                          name="GPSDIST"
                          value={Vehicle.GPSDIST}
                          onChange={handleChange}
                          required
                        />
                      </Form.Group>
                    </Col>
                  </Row>
                </div>

                {/* CLASSIFICATION */}
                <div className={`veh-section veh-advanced-tab-section ${activeAdvancedTab === "classification" ? "is-active" : ""}`}>
                  <div className="veh-section-title">
                    <i className="las la-tags text-primary" /> {translate("Classification")}
                  </div>
                  <Row className="g-3">
                    <Col md={6} className="veh-simple-space-fix">
                      <VehicleSelectGroup
                        controlId="category_vehicule"
                        name={"category_vehicule"}
                        label={translate("Category") + " " + translate("Vehicle")}
                        icon="search"
                        options={categoryOptions}
                        valueType={{
                          value: Vehicle.category_vehicule || "",
                          label: Vehicle.category_vehicule,
                        }}
                        onChange={handleCategoryChange}
                      />
                    </Col>

                    <Col md={6}>
                      <VehicleSelectGroup
                        controlId="type_vehicule"
                        name={"type_vehicule"}
                        label={translate("Vehicle type")}
                        icon="car"
                        options={typesOptions}
                        valueType={{
                          value: Vehicle.vehicule_type || "",
                          label: Vehicle.vehicule_type || "",
                        }}
                        onChange={handleTypeChange}
                      />
                    </Col>

                    <Col md={6}>
                      <Form.Group controlId="modele_vehicule" className="advanced-only">
                        <Form.Label>
                          <strong>{translate("Marque")}</strong> <span className="text-danger">*</span>
                        </Form.Label>
                        <Form.Control
                          type="text"
                          placeholder="Ex: Renault /Toyota /MAN ..."
                          name="modele_vehicule"
                          value={Vehicle.modele_vehicule}
                          onChange={handleChange}
                          required
                        />
                      </Form.Group>
                    </Col>

                    <Col md={6}>
                      <Form.Group controlId="propriete_vehicule" className="advanced-only">
                        <Form.Label>
                          <strong>{translate("Référence")}</strong> <span className="text-danger">*</span>
                        </Form.Label>
                        <Form.Control
                          type="text"
                          placeholder="Référence interne / externe"
                          name="propriete_vehicule"
                          value={Vehicle.propriete_vehicule || ""}
                          onChange={handleChange}
                          required
                        />
                      </Form.Group>
                    </Col>
                  </Row>
                </div>

                <div className={`veh-section veh-advanced-tab-section ${activeAdvancedTab === "assignment" ? "is-active" : ""}`}>
                  <div className="veh-section-title">
                    <i className="las la-id-card text-primary" /> {translate("Conducteur")} / {translate("Groupe")} / {translate("Dispositif")}
                  </div>

                  <Row className="g-3">
                    <Col md={6} className="veh-simple-space-fix">
                      <VehicleSelectGroup
                        controlId="user"
                        name={"user"}
                        label={translate("User")}
                        icon="user"
                        options={usersOptions}
                        valueType={{
                          value: Vehicle.id_user,
                          label: findUserById(Vehicle.id_user || 0)?.label || "",
                        }}
                        onChange={handleUserChange}
                      />
                    </Col>

                    <Col md={6}>
                      <VehicleSelectGroup
                        controlId="driver"
                        name={"Driver"}
                        label={translate("Driver")}
                        icon="user"
                        options={driversOptions}
                        valueType={{
                          value: Vehicle.id_conducteur,
                          label: findDriverById(Vehicle.id_conducteur || 0)?.label || "",
                        }}
                        onChange={handleDriverChange}
                      />
                    </Col>

                    <Col md={6}>
                      <VehicleSelectGroup
                        controlId="group"
                        name={"group"}
                        label={translate("Group")}
                        icon="users"
                        options={groupsOptions}
                        valueType={{
                          value: Vehicle.id_groupe,
                          label: findGroupById(Vehicle.id_groupe || 0)?.label || "",
                        }}
                        onChange={handleGroupChange}
                      />
                    </Col>

                    <Col md={6}>
                      <Form.Group controlId="PSN" className="advanced-only">
                        <Form.Label>
                         <strong>{translate("Dispositif")} (PSN) </strong> <span className="text-danger">*</span>
                        </Form.Label>
                        <Form.Control
                          type="text"
                          placeholder={translate("Select/enter device")}
                          name="PSN"
                          value={Vehicle.PSN}
                          onChange={handleChange}
                          required
                        />
                      </Form.Group>
                    </Col>
                  </Row>
                </div>

                {/* TECHNIQUE */}
                <div className={`veh-section veh-advanced-tab-section advanced-only ${activeAdvancedTab === "technical" ? "is-active" : ""}`}>
                  <div className="veh-section-title">
                    <i className="las la-tools text-primary" /> {translate("Technique")}
                  </div>
                  <Row className="g-3">
                    {technicalFields.map(renderAdvancedField)}
                  </Row>
                </div>

                {/* DOCUMENTS */}
                <div className={`veh-section veh-advanced-tab-section advanced-only ${activeAdvancedTab === "documents" ? "is-active" : ""}`}>
                  <div className="veh-section-title">
                    <i className="las la-file-contract text-primary" /> {translate("Documents")}
                  </div>

                  <div className="veh-subsection-title">{translate("Assurance")}</div>
                  <Row className="g-3">
                    {insuranceFields.map(renderAdvancedField)}
                  </Row>

                  <div className="veh-subsection-title mt-4">{translate("Contrôle technique et vignette")}</div>
                  <Row className="g-3">
                    {controlFields.map(renderAdvancedField)}
                  </Row>
                </div>

                {/* ENTRETIEN */}
                <div className={`veh-section veh-advanced-tab-section advanced-only ${activeAdvancedTab === "maintenance" ? "is-active" : ""}`}>
                  <div className="veh-section-title">
                    <i className="las la-oil-can text-primary" /> {translate("Entretien")}
                  </div>
                  <Row className="g-3">
                    {maintenanceFields.map(renderAdvancedField)}
                  </Row>
                </div>

                {/* APPARENCE */}
                <div className={`veh-section veh-advanced-tab-section ${activeAdvancedTab === "appearance" ? "is-active" : ""}`}>
                  <div className="veh-section-title">
                    <i className="las la-palette text-primary" /> {translate("Appearance")}
                  </div>

                  <Row className="g-3 align-items-end">
                    <Col md={6} className="veh-simple-space-fix">
                      <Form.Group controlId="formGroupColor" className="veh-color-field" style={{ fontSize: "14px" }}>
                        <Form.Label>
                           <strong>{translate("Color")}</strong> <span className="text-danger">*</span>
                        </Form.Label>
                        <div className="d-flex align-items-center gap-2">
                          <Form.Control
                            type="color"
                            value={Vehicle.couleur_vehicule}
                            onChange={(e) => handleVehicleChange({ couleur_vehicule: e.target.value })}
                            required
                            style={{ width: 70 }}
                          />
                          <Form.Control
                            type="text"
                            value={Vehicle.couleur_vehicule}
                            onChange={(e) => handleVehicleChange({ couleur_vehicule: e.target.value })}
                            pattern="#[0-9a-fA-F]{6}"
                            title="Veuillez entrer un code couleur hexadécimal valide (par exemple, #00ff00)"
                            required
                            style={{ maxWidth: 160 }}
                          />
                        </div>
                      </Form.Group>
                    </Col>
                  </Row>
                </div>

                {/* Footer actions */}
                <div className="veh-actions d-flex align-items-center justify-content-end gap-2">
                  <Button
                    onClick={() => {
                      setButtonClicked(true);
                      id_vehicule ? updateVehicle(Vehicle) : createVehicle(Vehicle);
                    }}
                    variant="primary"
                    type="submit"
                    disabled={buttonClicked}
                  >
                    {id_vehicule ? translate("Save changes") : translate("Create vehicle")}
                  </Button>

                  <Button onClick={cancelClicked} type="button" variant="outline-secondary" disabled={buttonClicked}>
                    {translate("Cancel")}
                  </Button>
                </div>
              </Form>
            </Card>
          </div>

          {/* RIGHT: Sidebar recap */}
          <div className="col-lg-4">
            <div className="veh-sidebar">
              <Card className="veh-sidebar-card mb-3">
                <div className="veh-sidebar-header">
                  <div className="d-flex align-items-center justify-content-between gap-2">
                    <div>
                      <div className="fw-bold">{translate("Vehicle user assignments")}</div>
                      <div className="text-muted" style={{ fontSize: 12 }}>
                        {translate("Multiple users assigned through the new table")}
                      </div>
                    </div>
                  </div>
                </div>

                <div className="veh-kv">
                  <Form.Label className="fw-bold" style={{ fontSize: 13 }}>
                    {translate("Search user")}
                  </Form.Label>
                  <Select
                    classNamePrefix="react-select"
                    options={availableAffectationUsers}
                    value={null}
                    onChange={addAffectationUser}
                    getOptionLabel={(option: OptionType) => option.label}
                    getOptionValue={(option: OptionType) => String(option.value)}
                    placeholder={translate("Search and select a user")}
                    noOptionsMessage={() => translate("No user found")}
                    isClearable={false}
                    isSearchable
                    menuPortalTarget={document.body}
                    menuPosition="fixed"
                    styles={{
                      menu: (base) => ({ ...base, zIndex: 9999 }),
                      menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                      placeholder: (base) => ({ ...base, color: "#6b7280" }),
                    }}
                  />

                  {selectedAffectationUsers.length === 0 && (
                    <div className="veh-empty-tags">
                      {translate("No assignment added")}
                    </div>
                  )}

                  {selectedAffectationUsers.length > 0 && (
                    <div className="veh-tag-list">
                      {selectedAffectationUsers.map((user) => (
                        <span key={user.value} className="veh-user-tag">
                          <i className="bi bi-person" />
                          <span>{user.label}</span>
                          <button type="button" onClick={() => removeAffectationUser(user.value)} aria-label={translate("Remove")}>
                            <i className="bi bi-x" />
                          </button>
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              </Card>

              <Card className="veh-sidebar-card veh-recap-card">
                <div className="veh-sidebar-header advanced-only">
                  <div className="d-flex align-items-center justify-content-between">
                    <div className="fw-bold">{translate("Récap configuration")}</div>
                    {statusChip()}
                  </div>

                  <div className="text-muted" style={{ fontSize: 12, marginTop: 4 }}>
                    {translate("État représentatif de la configuration")}
                  </div>

                  <div style={{ marginTop: 12 }}>
                    <div className="d-flex justify-content-between" style={{ marginBottom: 6 }}>
                      <small className="text-muted">{translate("Complétion")}</small>
                      <small className="fw-bold">{completion.percent}%</small>
                    </div>
                    <ProgressBar now={completion.percent} variant={progressVariant as any} style={{ height: 8, borderRadius: 999 }} />
                    {completion.missing.length > 0 && (
                      <div className="mt-3">
                        <div className="text-muted" style={{ fontSize: 12, marginBottom: 6 }}>
                          Champs manquants :
                        </div>
                        <div className="d-flex flex-wrap gap-2">
                          {completion.missing.map((m: string) => (
                            <span key={m} className="badge text-bg-light border">
                              {m}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                <div className="veh-kv advanced-only">
                  <div className="veh-kv-row">
                    <div className="veh-k">{translate("Immatriculation")}</div>
                    <div className="veh-v">{safeText(Vehicle.immatriculation_vehicule)}</div>
                  </div>
                  <div className="veh-kv-row">
                    <div className="veh-k">{translate("N° porte")}</div>
                    <div className="veh-v">{safeText(Vehicle.num_porte_vehicule)}</div>
                  </div>
                  <div className="veh-kv-row">
                    <div className="veh-k">{translate("VIN")}</div>
                    <div className="veh-v">{safeText(Vehicle.num_chassis_vehicule)}</div>
                  </div>
                  <div className="veh-kv-row">
                    <div className="veh-k">{translate("Catégorie")}</div>
                    <div className="veh-v">{safeText(Vehicle.category_vehicule)}</div>
                  </div>
                  <div className="veh-kv-row">
                    <div className="veh-k">{translate("Type")}</div>
                    <div className="veh-v">{safeText(Vehicle.vehicule_type)}</div>
                  </div>
                  <div className="veh-kv-row">
                    <div className="veh-k">{translate("Marque")}</div>
                    <div className="veh-v">{safeText(Vehicle.modele_vehicule)}</div>
                  </div>
                  <div className="veh-kv-row">
                    <div className="veh-k">{translate("Référence")}</div>
                    <div className="veh-v">{safeText(Vehicle.propriete_vehicule)}</div>
                  </div>
                  <div className="veh-kv-row">
                    <div className="veh-k">{translate("Utilisateur")}</div>
                    <div className="veh-v">{safeText(findUserById(Vehicle.id_user || 0)?.label)}</div>
                  </div>
                  <div className="veh-kv-row">
                    <div className="veh-k">{translate("Groupe")}</div>
                    <div className="veh-v">{safeText(findGroupById(Vehicle.id_groupe || 0)?.label)}</div>
                  </div>
                  <div className="veh-kv-row">
                    <div className="veh-k">{translate("Dispositif")}</div>
                    <div className="veh-v">{safeText(Vehicle.PSN)}</div>
                  </div>
                  <div className="veh-kv-row">
                    <div className="veh-k">{translate("Couleur")}</div>
                    <div className="veh-v">
                      <span className="veh-dot" style={{ background: Vehicle.couleur_vehicule || "#d1d5db" }} />
                      <span style={{ marginLeft: 8 }}>{safeText(Vehicle.couleur_vehicule)}</span>
                    </div>
                  </div>
                </div>

                <div className="px-3 pb-3 veh-sidebar-tools">
                  <div className="text-muted" style={{ fontSize: 12, marginBottom: 10 }}>
                    <i className="bi bi-info-circle" /> {translate("Validation configuration uniquement")}
                  </div>

                  <div className="d-grid gap-2">
                    <Button variant="outline-primary" type="button" onClick={() => toast.info(translate("Export (à brancher)"))}>
                      <i className="bi bi-download" /> {translate("Exporter fiche")}
                    </Button>

                    <Button variant="outline-secondary" type="button" onClick={() => window.print()}>
                      <i className="bi bi-printer" /> {translate("Imprimer")}
                    </Button>

                    <Button
                      variant="outline-secondary"
                      type="button"
                      onClick={async () => {
                        try {
                          await navigator.clipboard.writeText(Vehicle.propriete_vehicule || "");
                          toast.success(translate("Copied"), { position: "bottom-right", autoClose: 1400, transition: Bounce });
                        } catch {
                          toast.warn(translate("Copy failed"), { position: "bottom-right", autoClose: 1400, transition: Bounce });
                        }
                      }}
                    >
                      <i className="bi bi-clipboard" /> {translate("Copier référence")}
                    </Button>
                  </div>
                </div>
              </Card>
            </div>
          </div>

        </div>
      </div>
    </>
  );
};

export default Vehicle;
